// Mirrors api/utils/chatFaults.js. The backend answers dead conversational
// surfaces with one of these fixed codes over JSON or SSE, so both client
// boundaries must recognise the same taxonomy without inspecting messages.
export const CHAT_UNAVAILABLE_CODE = 'chat_vendor_unavailable';
const CHAT_UNAVAILABLE_CODES = Object.freeze(new Set([
  'chat_vendor_unavailable',
  'chat_storage_unavailable',
  'chat_auth_expired',
  'chat_conversation_unresolvable',
  'chat_actions_may_have_completed',
]));

const isKnownChatUnavailableCode = (code) => (
  typeof code === 'string' && CHAT_UNAVAILABLE_CODES.has(code)
);

const isChatFaultEnvelope = (error) => Boolean(
  error &&
  typeof error === 'object' &&
  isKnownChatUnavailableCode(error.code)
);

export const isChatUnavailableError = (error) => {
  if (!error) return false;

  // Axios rejection from a JSON endpoint.
  if (isChatFaultEnvelope(error?.response?.data?.error)) return true;

  // An already-parsed payload, from an SSE frame or a resolved body.
  if (isChatFaultEnvelope(error?.error)) return true;

  return false;
};

// SSE frames arrive as raw strings; a chat stream that ends in this frame has
// failed, even though the HTTP status said 200 long before the failure.
export const isChatUnavailablePayload = (payload) => {
  if (!payload) return false;

  if (typeof payload === 'string') {
    try {
      return isChatUnavailablePayload(JSON.parse(payload));
    } catch {
      return false;
    }
  }

  return isChatFaultEnvelope(payload?.error);
};

export const responseToChatUnavailableError = async (response) => {
  const error = new Error('Chat request failed');

  try {
    error.response = { data: await response.json() };
  } catch {
    // A non-JSON response is still a request error, but it has no fault code
    // to turn into a degraded chat surface.
  }

  return error;
};
